import { createSelector } from "reselect";
import { getSiteList } from 'modules/cbre-flow-view-management/ducks/selectors';
const getConfigInfo = (state) => state && state.config;
const getApiDucksInfo = (state) => state && state['cbre-flow-api-ducks'];
const getAuthInfo = (state) => state && state.auth;
const getState = (state) => state;


export const getUserInfoUrl = createSelector(
    [getConfigInfo],
    (configInfo) =>
        configInfo && configInfo.client && configInfo.client.flowApiUrl
            ? `${configInfo.client.flowApiUrl}api/users/config`
            : '',
);

export const getUserConfigData = createSelector(
    [getApiDucksInfo],
    (apiDucksInfo) => {
        const { userInfo } = apiDucksInfo?.UserInfo || {};
        const { userConfig } = userInfo || {};
        return userConfig || [];
    }
);

export const getUserDetails = createSelector(
    [getApiDucksInfo],
    (apiDucksInfo) => {
        const { userInfo } = apiDucksInfo?.UserInfo || {};
        const { userDetails, userId } = userInfo|| {};
        return { ...(userDetails || {}), userId } || {};
    }
);

const emptyObject = {};

const mapSavedLookupOption = (saved, accessKey = 'name') => {
    if (!saved) return null;
    if (typeof saved === 'object') return saved;
    return { [accessKey]: saved, name: saved, label: saved, value: saved };
};

const mapSavedLookupArray = (saved, accessKey = 'name') => {
    if (!saved) return [];
    return (Array.isArray(saved) ? saved : [saved]).map((item) => mapSavedLookupOption(item, accessKey)).filter(Boolean);
};

// Reverse-maps a saved single-select building/floor/room value (or legacy array)
// into { value, label, isInclude }, so callers can build their literal-keyed form option.
const parseSavedLocationValue = (saved) => {
    const item = Array.isArray(saved) ? saved[0] : saved;
    if (!item) return null;
    if (typeof item === 'object') {
        return { value: item?.value, label: item?.label, isInclude: !!item?.isInclude };
    }
    return { value: item, label: item, isInclude: false };
};

export const getUserAccess = createSelector(
    [getUserDetails, getAuthInfo, getSiteList],
    (userDetails, authInfo, accessSites) => {
        const { currentContext: { permissions = [], role = emptyObject, site = {}, client = { }, isPortFolioDefaultViewCreated, hasRequestedSiteAccess } = {}, clients = [], userId } = userDetails || emptyObject;
        const isSystemAdmin = role.translationKey === "SYS_ADMIN";
        const isAnyAdmin = role.translationKey === "SYS_ADMIN" || role.translationKey === "CBRE_ACCOUNT_ADMIN" || role.translationKey === "CBRE_FS_COORDINATOR";
        client.clientCode = client.clientCode || authInfo.activeClientCode;
        if(client.clientCode && clients.length > 0 && !clients.clientName) {
            const matchedClient = clients.find(c => c.clientUniqueId === client.clientCode);
            if(matchedClient) {
                client.clientName = matchedClient.clientName
            };
        }
        if(client.clientCode === "CBRE_DEFAULT") client.clientCode = "CBRE";
        const hasAnyEditSite = (accessSites || []).some(s => s.accessLevel === "EDIT" && s.accessStatus === "GRANTED");
        return { 
            hasInstrumentPageAccess: permissions.includes("MANAGE_INSTRUMENT_MODULE") || isSystemAdmin,
            hasInstrumentEditAccess: (permissions.includes("MANAGE_INSTRUMENT_MODULE") && hasAnyEditSite) || isSystemAdmin,
            hasWorkRequestPageAccess: permissions.includes("MANAGE_REQUEST_MODULE") || isSystemAdmin,
            hasWorkRequestEditAccess: (permissions.includes("MANAGE_REQUEST_MODULE") && hasAnyEditSite) || isSystemAdmin,
            hasQuickLink1Access: permissions.includes("QUICK_LINKS_1") || isSystemAdmin,
            hasQuickLink2Access: permissions.includes("QUICK_LINKS_2") || isSystemAdmin,
            hasEntitlementReviewerWindowAccess: role.translationKey && !isAnyAdmin,
            hasEntitlementReviewWindowAccess: isAnyAdmin ,
            hasApprovalPageAccess: permissions.includes("APPROVAL_ENGINE_ACCESS") || isSystemAdmin,
            hasUserAndRolePageAccess: permissions.includes("MANAGE_CLIENT_USER_SITE_ACCESS") || isSystemAdmin,
            isSystemAdmin,
            isAnyAdmin,
            site,
            client,
            clients,
            role,
            hasEditAccess: hasAnyEditSite,
            hasSiteAccess: isSystemAdmin || (!!site.accessLevel && site.accessLevel !== "NO_ACCESS" && site.accessStatus !== "PENDING"),
            hasCurrentViewAccess: isSystemAdmin || isPortFolioDefaultViewCreated,
            hasRequestedSiteAccess,
            isSiteForApproval: site.accessStatus === "PENDING",
            userId, 
            appCode: 'ELIS'
         };
        
    }
);

export const getUserInstrumentConfig = createSelector(
    [getUserConfigData],
    (userConfig) => {
        const instrumentFilterConfig = userConfig?.filter(config => config?.configType === "INSTRUMENT_FILTER");
        const instrumentConfigValues = instrumentFilterConfig?.length > 0 ? instrumentFilterConfig[0].configValue : emptyObject;
        const parsedBuilding = parseSavedLocationValue(instrumentConfigValues?.c_buildingname);
        const parsedFloor = parseSavedLocationValue(instrumentConfigValues?.c_floorlevel);
        const parsedRoom = parseSavedLocationValue(instrumentConfigValues?.c_roomspace);
        const mappedConfigValues = {
           requestedBy: instrumentConfigValues?.requestedBy? {email: instrumentConfigValues?.requestedBy} : '',
            instrumentDescription: instrumentConfigValues?.c_assetdescription || '',
            system: instrumentConfigValues?.c_system_c_systemname || [],
            status: instrumentConfigValues?.c_availablestatus? instrumentConfigValues?.c_availablestatus : [],
            siteTeam: instrumentConfigValues?.c_assetservicegroup? instrumentConfigValues?.c_assetservicegroup : [],
            manufacturer: Array.isArray(instrumentConfigValues?.c_manufacturer)
                ? instrumentConfigValues.c_manufacturer.map((item) =>
                    typeof item === 'object'
                        ? { c_make: item?.value, __isIncludeOption: !!item?.isInclude }
                        : { c_make: item },
                  )
                : instrumentConfigValues?.c_manufacturer ? {c_make: instrumentConfigValues?.c_manufacturer}: '',
            instrumentOwner: Array.isArray(instrumentConfigValues?.c_instrumentowner)
                ? instrumentConfigValues.c_instrumentowner.map((item) =>
                    typeof item === 'object'
                        ? { name: item?.value, __isIncludeOption: !!item?.isInclude }
                        : { name: item },
                  )
                : instrumentConfigValues?.c_instrumentowner?.email
                ? instrumentConfigValues?.c_instrumentowner
                : instrumentConfigValues?.c_instrumentowner
                    ? { name: instrumentConfigValues?.c_instrumentowner }
                    : null,
            backupInstrumentOwner: Array.isArray(instrumentConfigValues?.c_backupinstrumentowner)
                ? instrumentConfigValues.c_backupinstrumentowner.map((item) =>
                    typeof item === 'object'
                        ? { name: item?.value, __isIncludeOption: !!item?.isInclude }
                        : { name: item },
                  )
                : instrumentConfigValues?.c_backupinstrumentowner?.email
                ? instrumentConfigValues?.c_backupinstrumentowner
                : instrumentConfigValues?.c_backupinstrumentowner
                    ? { name: instrumentConfigValues?.c_backupinstrumentowner }
                    : null,
            modelNumber: Array.isArray(instrumentConfigValues?.c_modelnumber)
                ? instrumentConfigValues.c_modelnumber.map((item) =>
                    typeof item === 'object'
                        ? { c_model: item?.value, __isIncludeOption: !!item?.isInclude }
                        : { c_model: item },
                  )
                : instrumentConfigValues?.c_modelnumber? {c_model: instrumentConfigValues?.c_modelnumber} : '',
            serialNumber: instrumentConfigValues?.c_serialnumber || '',
            assetClass: instrumentConfigValues?.c_assetclass || [],
            assetType: instrumentConfigValues?.c_assetsubclass || [],
            site: instrumentConfigValues?.c_site? {siteName: instrumentConfigValues?.c_site} : '',
            building: parsedBuilding ? { c_buildingname: parsedBuilding.value, label: parsedBuilding.label, __isIncludeOption: parsedBuilding.isInclude } : '',
            floorLevel: parsedFloor ? { c_floorarea: parsedFloor.value, label: parsedFloor.label, __isIncludeOption: parsedFloor.isInclude } : '',
            roomSpace: parsedRoom ? { c_roomspace: parsedRoom.value, label: parsedRoom.label, __isIncludeOption: parsedRoom.isInclude } : '',
            department: instrumentConfigValues?.c_department? instrumentConfigValues?.c_department: [],
            gxpStatus: instrumentConfigValues?.c_gxpstatus? instrumentConfigValues?.c_gxpstatus : [],
            costCenter: Array.isArray(instrumentConfigValues?.c_clientcostcenter)
                ? instrumentConfigValues.c_clientcostcenter.map((item) =>
                    typeof item === 'object'
                        ? { name: item?.value, __isIncludeOption: !!item?.isInclude }
                        : { name: item },
                  )
                : instrumentConfigValues?.c_clientcostcenter ? { name: instrumentConfigValues?.c_clientcostcenter } : null,
            purchaseCost: instrumentConfigValues?.c_purchasecost || '',
            financialId: instrumentConfigValues?.financialId || '',
            installDate: instrumentConfigValues?.c_installdate || null,
            endOfWarrantyDate: instrumentConfigValues?.c_endofwarranty || null,
            businessCriticality: instrumentConfigValues?.c_clientbusinessimpact ? instrumentConfigValues?.c_clientbusinessimpact : [],
            localDesignation: instrumentConfigValues?.c_clientcriticalitylevel ? instrumentConfigValues?.c_clientcriticalitylevel : []
        }
        return {mappedConfigValues, instrumentConfigValues};
    }
);

export const getEntitlementDetailInstrumentConfig = createSelector(
    [getUserConfigData],
    (userConfig) => {
        const instrumentFilterConfig = userConfig?.filter(config => config?.configType === "INSTRUMENT_ERW_FILTER");
        const instrumentConfigValues = instrumentFilterConfig?.length > 0 ? instrumentFilterConfig[0].configValue : emptyObject;
        const mappedConfigValues = {
            system: instrumentConfigValues?.c_system_c_systemname || [],
            status: instrumentConfigValues?.c_availablestatus? instrumentConfigValues?.c_availablestatus : [],
            siteTeam: instrumentConfigValues?.c_assetservicegroup? instrumentConfigValues?.c_assetservicegroup : [],
            manufacturer: instrumentConfigValues?.c_manufacturer? {c_make: instrumentConfigValues?.c_manufacturer}: '',
            instrumentOwner: instrumentConfigValues?.c_instrumentowner?.email ? instrumentConfigValues?.c_instrumentowner:  null,
            instrumentReviewer: instrumentConfigValues?.c_instrumentreviewer?.email ? instrumentConfigValues?.c_instrumentreviewer:  null,
            modelNumber: instrumentConfigValues?.c_modelnumber? {c_model: instrumentConfigValues?.c_modelnumber} : '',
            serialNumber: instrumentConfigValues?.c_serialnumber || '',
            assetClass: instrumentConfigValues?.c_assetclass || [],
            assetType: instrumentConfigValues?.c_assetsubclass || [],
            site: instrumentConfigValues?.c_site? {siteName: instrumentConfigValues?.c_site} : '',
            building: instrumentConfigValues?.c_buildingname? {c_buildingname: instrumentConfigValues?.c_buildingname}: '',
            floorLevel: instrumentConfigValues?.c_floorlevel? {c_floorarea: instrumentConfigValues?.c_floorlevel}: '',
            roomSpace: instrumentConfigValues?.c_roomspace? {c_roomspace: instrumentConfigValues?.c_roomspace}: '',
            department: instrumentConfigValues?.c_department? instrumentConfigValues?.c_department: [],
            gxpStatus: instrumentConfigValues?.c_gxpstatus? instrumentConfigValues?.c_gxpstatus : [],           
            businessCriticality: instrumentConfigValues?.c_clientbusinessimpact ? instrumentConfigValues?.c_clientbusinessimpact : [],
            
        }
        return {mappedConfigValues, instrumentConfigValues};
    }
);

export const getUserApprovalFilterConfig = createSelector(
    [getUserConfigData],
    (userConfig) => {
        const approvalFilterConfig = userConfig?.filter(config => config?.configType === "APPROVAL_FILTER");
        const approvalConfigValues = approvalFilterConfig?.length > 0 ? approvalFilterConfig[0].configValue : emptyObject;
        const mappedConfigValues = {
            requestor: approvalConfigValues?.requestor || [],
            system: approvalConfigValues?.system || [],
            status: approvalConfigValues?.status || [],
            approvalCategory: approvalConfigValues?.approvalCategory || [],
            assetClass: approvalConfigValues?.assetClass || [],
            assetType: approvalConfigValues?.assetType|| [],
            site: approvalConfigValues?.site || null,
            building: approvalConfigValues?.building|| [],
            floorLevel: approvalConfigValues?.floorLevel || [],
            roomSpace: approvalConfigValues?.roomSpace || [],
            department: approvalConfigValues?.department || [],
            gxpStatus: approvalConfigValues?.gxpStatus || [],
            costCenter: approvalConfigValues?.costCenter? approvalConfigValues?.costCenter : [],
            isPastDue: approvalConfigValues?.isPastDue? approvalConfigValues?.isPastDue : false,
        }
        return {mappedConfigValues, approvalConfigValues};
    }
);

export const getUserUpComingMaintenanceConfig = createSelector(
    [getUserConfigData],
    (userConfig) => {
        const instrumentFilterConfig = userConfig?.filter(config => config?.configType === "UPCOMING_MAINT_FILTER");
        // Filters saved earlier used c_requesttype (request type); the filter is now on work order type, so drop the legacy key.
        const savedConfigValues = (instrumentFilterConfig?.length > 0 ? instrumentFilterConfig[0].configValue : emptyObject) || emptyObject;
        const { c_requesttype: legacyRequestType, ...instrumentConfigValues } = savedConfigValues;
        const mappedConfigValues = {
            workType: instrumentConfigValues?.c_workordertype_data? instrumentConfigValues?.c_workordertype_data : [],
            assetId: instrumentConfigValues?.assetId? instrumentConfigValues?.assetId : null,
            primaryAssignment: Array.isArray(instrumentConfigValues?.c_primaryassignment)
                ? instrumentConfigValues.c_primaryassignment
                : instrumentConfigValues?.c_primaryassignment?.email
                ? {
                    ...instrumentConfigValues.c_primaryassignment,
                    name: instrumentConfigValues.c_primaryassignment.c_fullname || instrumentConfigValues.c_primaryassignment.email,
                    label: instrumentConfigValues.c_primaryassignment.c_fullname || instrumentConfigValues.c_primaryassignment.email,
                }
                : null,
            workRequestId: instrumentConfigValues?.work_requestId? instrumentConfigValues?.work_requestId : null,
            serviceProvider: instrumentConfigValues?.c_serviceprovider? instrumentConfigValues?.c_serviceprovider : [],
        }
        return {mappedConfigValues, instrumentConfigValues};
    }
);

export const getUserWorkRequestConfig = createSelector(
    [getUserConfigData, getState],
    (userConfig, state) => {
        const { site: userSelectedSite } = getUserAccess(state);
        const instrumentFilterConfig = userConfig?.filter(config => config?.configType === "WORKREQUEST_FILTER");
        const instrumentConfigValues = instrumentFilterConfig?.length > 0 ? instrumentFilterConfig[0].configValue : emptyObject;
        const parsedBuilding = parseSavedLocationValue(instrumentConfigValues?.request_td_buildingname);
        const parsedFloor = parseSavedLocationValue(instrumentConfigValues?.request_td_floorlevel);
        const parsedRoom = parseSavedLocationValue(instrumentConfigValues?.request_td_roomspace);
        const mappedConfigValues = {
            requestedBy: mapSavedLookupArray(instrumentConfigValues?.requestedBy),
            instrumentDescription: instrumentConfigValues?.c_assetdescription || '',
            system: instrumentConfigValues?.c_system_c_systemname || [],
            status: mapSavedLookupArray(instrumentConfigValues?.workrequest_timeline_status, 'uniqueCode'),
            manufacturer: instrumentConfigValues?.c_manufacturer? {c_make: instrumentConfigValues?.c_manufacturer}: '',
            instrumentOwner: instrumentConfigValues?.c_instrumentowner?.email ? instrumentConfigValues?.c_instrumentowner:  null,
            modelNumber: instrumentConfigValues?.c_modelnumber? {c_model: instrumentConfigValues?.c_modelnumber} : '',
            serialNumber: instrumentConfigValues?.c_serialnumber || '',
            assetClass: instrumentConfigValues?.c_assetclass || [],
            assetType: instrumentConfigValues?.c_assettype || [],
            site: instrumentConfigValues?.c_site
                ? mapSavedLookupOption(instrumentConfigValues.c_site, 'site')
                : userSelectedSite,
            building: parsedBuilding ? { c_buildingname: parsedBuilding.value, label: parsedBuilding.label || parsedBuilding.value, __isIncludeOption: parsedBuilding.isInclude } : '',
            floorLevel: parsedFloor ? { c_floorarea: parsedFloor.value, label: parsedFloor.label || parsedFloor.value, __isIncludeOption: parsedFloor.isInclude } : '',
            roomSpace: parsedRoom ? { c_roomspace: parsedRoom.value, label: parsedRoom.label || parsedRoom.value, __isIncludeOption: parsedRoom.isInclude } : '',
            department: instrumentConfigValues?.c_department? instrumentConfigValues?.c_department : [],
            gxpStatus: mapSavedLookupArray(instrumentConfigValues?.c_gxpstatus, 'uniqueCode'),
            businessCriticality: mapSavedLookupArray(instrumentConfigValues?.c_clientbusinessimpact),
            localDesignation: mapSavedLookupArray(instrumentConfigValues?.c_clientcriticalitylevel),
            purchaseCost: instrumentConfigValues?.c_purchasecost || '',
            financialId: instrumentConfigValues?.financialId || '',
            installDate: instrumentConfigValues?.c_installdate || null,
            endOfWarrantyDate: instrumentConfigValues?.c_endofwarranty || null,
            requestType: instrumentConfigValues?.requestType || [],
            costCenter: mapSavedLookupArray(
                instrumentConfigValues?.request_td_clientcostcenter || instrumentConfigValues?.c_clientcostcenter,
            ),
        }
        return {mappedConfigValues, instrumentConfigValues};
    }
);

const emptyArray = [];

export const getUserInstrumentDetailsWorkRequestConfig = createSelector(
    [getUserConfigData, getState],
    (userConfig, state) => {
        const instrumentFilterConfig = userConfig?.filter(config => config?.configType === "INSTRUMENT_DETAILS_WORKREQUEST_FILTER");
        const instrumentConfigValues = instrumentFilterConfig?.length > 0 ? instrumentFilterConfig[0].configValue : emptyObject;
        const mappedConfigValues = {
            requestedBy: instrumentConfigValues?.requestedBy?.email ?  instrumentConfigValues?.requestedBy : null,
            assignedTo: instrumentConfigValues?.assignedTo?.email ?  instrumentConfigValues?.assignedTo : null,
            instrumentDescription: instrumentConfigValues?.c_assetdescription || '',
          
            status: instrumentConfigValues?.workrequest_timeline_status? instrumentConfigValues?.workrequest_timeline_status : emptyArray,
            dueDate: instrumentConfigValues?.dueDate || null,
            requestType: instrumentConfigValues?.requestType || emptyArray,
        }
        return {mappedConfigValues, instrumentConfigValues};
    }
);

export const getUserRoleConfig = createSelector(
    [getUserConfigData],
    (userConfig) => {
        const userRoleConfig = userConfig?.filter(config => config?.configType === "CLIENT_USERS_FILTERS");
        const userRoleFilterValues = userRoleConfig?.length > 0 ? userRoleConfig[0].configValue : emptyObject;
        const mappedConfigValues = {
            primaryRoles: userRoleFilterValues?.primaryRoles || [],
            editableSites: userRoleFilterValues?.editableSites || [],
            readOnlySites: userRoleFilterValues?.readOnlySites || [],
        }
        return {mappedConfigValues, userRoleFilterValues};
    }
);

export const getUserGroupActiveClientConfig = createSelector(
    [getUserConfigData, getUserDetails],
    (userConfig, userDetails) => {
        const userGroupActiveClientConfig = userConfig?.find(
            (config) => config?.configType === "USER_GROUP_ACTIVE_CLIENT"
        );
        const activeClientConfig = userConfig?.find(
            (config) => config?.configType === "ACTIVE_CLIENT"
        );

        const selectedConfig = userGroupActiveClientConfig || activeClientConfig || emptyObject;
        const selectedConfigValue = selectedConfig?.configValue || emptyObject;

        const activeClientCode =
            selectedConfigValue?.activeClientCode ||
            selectedConfig?.activeClientCode ||
            null;

        return {
            activeClientCode,
            userId: userDetails?.userId || null,
            configType: selectedConfig?.configType || null,
        };
    }
);
